// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

import { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductPriceStyles } from "@rugby/Overrides/Components/ProductPrice";
import { screen } from "@rugby/XDL/Helpers";
import { color, ellipsis, MediaQuery } from "@rugby/XDL/Utilities";
import { styledScrollbar } from "@rugby/XDL/Utilities/SharedStyles";

interface MiniCartStyles {
    miniCartButton: InjectableCss;
    cartIcon: InjectableCss;
    itemsNumber: InjectableCss;
    subTotal: InjectableCss;
    wrapper: InjectableCss;
    title: TypographyPresentationProps;
    content: InjectableCss;
    line: InjectableCss;
    lineContent: InjectableCss;
    lineImage: InjectableCss;
    linePriceWrapper: InjectableCss;
    linePriceUOMWrapper: InjectableCss;
    linePrice: ProductPriceStyles;
    lineInput: InjectableCss;
    lineTotalPrice: TypographyPresentationProps;
    trashIcon: IconPresentationProps;
    actionWrapper: InjectableCss;
    quantity: CartLineQuantityStyles;
    cartZeroPricedWrapper: InjectableCss;
    cartProductsCannotBePurchased: InjectableCss;
    buttonsWrapper: InjectableCss;
    buttons: InjectableCss;
    existLineNeedAdditionalInfo?: InjectableCss;
    lineDescription: LinkPresentationProps;
}

export const quantityInputHeightMiniCart = 34;

export const miniCartStyles: MiniCartStyles = {
    miniCartButton: {
        css: css`
            margin-bottom: 5px;
            &:focus {
                outline: none;
            }
            & > span {
                display: flex;
                align-items: flex-end;
                flex-direction: row;
                cursor: pointer;

                &:hover svg,
                &:hover > span {
                    background-color: ${getColor("secondary.main")};
                }
            }
        `,
    },
    cartIcon: {
        css: css`
            background-color: ${({ isActive }: any) =>
                isActive ? getColor("secondary.main") : getColor("common.brand3")};

            color: ${getColor("common.white")};
            height: 30px;
            width: 30px;
            border-radius: 3px;
            border-radius: 3px;
            padding: 4px;
            margin-right: 8px;

            ${screen("lte", "sm")`
               height: 40px;
               width: 40px;
               svg {
                height: 40px;
                width: 40px;
               }
            `}
        `,
    },
    itemsNumber: {
        css: css`
            color: ${getColor("text.main")};
            font-size: 13px;
            font-weight: bold;
            text-transform: capitalize;
            letter-spacing: 0;
            line-height: 17px;
        `,
    },
    subTotal: {
        css: css`
            color: ${getColor("text.main")};
            font-size: 13px;
            text-transform: capitalize;
            letter-spacing: 0;
            line-height: 17px;
        `,
    },
    wrapper: {
        css: css`
            width: 514px;
            max-width: 514px;
            ${screen("lte", "sm")`
                max-width: 100%;
                height: 100vh;
                display: flex;
                flex-direction: column;
            `}
        `,
    },
    title: {
        color: "text.main",
        size: 28,
        css: css`
            letter-spacing: 0;
            line-height: 30px;

            ${screen("lte", "sm")`
                display: block;
                padding: 20px 10px;
            `}
        `,
    },
    content: {
        css: css`
            margin-top: 15px;
            border-top: 1px solid ${getColor("common.border")};
            max-height: calc(133px * 3);
            padding: 10px 8px 10px 0;
            overflow: auto;

            ${styledScrollbar()}

            ${screen("lte", "md")`
                margin-top: 0;
                max-height: calc(100vh - 200px);
                overflow: auto;

                & div {
                    max-height: initial !important;
                }
            `}
        `,
    },
    line: {
        css: css`
            padding: 20px 0;
            display: flex;
            border-bottom: 1px solid ${getColor("common.border")};

            ${screen("lte", "sm")`
                flex-direction: column;
                padding: 20px 15px;
                align-items: center;
            `}
        `,
    },
    lineImage: {
        css: css`
            height: 92px;
            width: 115px;
            max-height: 115px;
            border: 1px solid ${getColor("common.border")};
            margin-right: 20px;
            line-height: 12px;
            font-size: 10px;
            ${screen("lte", "md")`
                margin-bottom: 20px;
            `}

            & > a {
                display: block;
                height: 100%;
                text-align: center;
            }

            img {
                width: auto;
                height: auto;
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },

    lineContent: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;

            ${screen("lte", "md")`
                width: 100%;
            `}
        `,
    },
    lineDescription: {
        color: "secondary.main",
        typographyProps: {
            size: 16,
            css: css`
                font-size: 16px;
                letter-spacing: 0;
                line-height: 22px;
                ${ellipsis(2, 22)}
            `,
        },
        hoverStyle: {
            color: color("text.link", 0.8)(),
        },
        css: css`
            width: 100%;
            margin-bottom: 20px;

            // to remove default top margin
            margin-top: -5px;
        `,
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            ${screen("lte", "sm")`
               flex-wrap: wrap;
            `}
        `,
    },
    quantity: {
        editableQuantityTextField: {
            cssOverrides: {
                formField: css`
                    margin-right: 2px;
                    height: ${quantityInputHeightMiniCart}px;
                    width: 76px;
                `,
                inputSelect: css`
                    height: ${quantityInputHeightMiniCart}px;
                `,
            },
        },
    },
    linePriceWrapper: {
        css: css`
            ${screen("lte", "sm")`
               max-width: 100px;
            `}
        `,
    },
    linePriceUOMWrapper: {
        css: css`
            ${screen("lte", "sm")`
               max-width: 80px;
               padding-top: 8px;
               order: 4;
            `}
        `,
    },
    linePrice: {
        price: {
            priceText: {
                size: 14,
                css: css`
                    font-weight: bold;
                    letter-spacing: 0;
                    line-height: 17px;
                `,
            },
            unitOfMeasureText: {
                size: 14,
                css: css`
                    font-weight: bold;
                    letter-spacing: 0;
                    line-height: 17px;
                `,
            },
        },
    },
    lineInput: {
        css: css`
            height: 34px;
            width: 95px;
        `,
    },
    lineTotalPrice: {
        size: 14,
        color: "text.main",
        css: css`
            font-weight: bold;
            letter-spacing: 0;
            line-height: 17px;
            margin-right: 5px;
        `,
    },
    trashIcon: {
        size: 14,
        color: "secondary",
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            margin-top: 25px;

            ${screen("lte", "sm")`
               padding: 0 15px;
               margin-top: auto;
            `}
        `,
    },
    cartZeroPricedWrapper: {
        css: css`
            margin-top: 15px;
            ${screen("lte", "sm")`
               margin: 15px 15px 10px 15px;
            `}
        `,
    },
    cartProductsCannotBePurchased: {
        css: css`
            margin-top: 15px;
            ${screen("lte", "sm")`
               margin: 15px;
            `}
        `,
    },
    buttons: {
        css: css`
            width: calc(50% - 3px);
            ${({ theme }: any) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                            margin-bottom: 10px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};
        `,
    },
    existLineNeedAdditionalInfo: {
        css: css`
            margin-top: 8px;
            margin-bottom: 16px;
        `,
    },
};
